import { useCallback, useState } from 'react';
import { InteractionManager, Platform } from 'react-native';

import { useAuth } from '../contexts/AuthContext';
import { openProWelcome } from '../core/navigation/proWelcomeController';
import { closeSettingsOverlay } from '../core/navigation/settingsOverlayController';
import { ApiError } from '../services/apiClient';
import {
  isAppleIapUserCancelled,
  openAppleSubscriptions,
  purchaseProSubscription,
  restoreProPurchases,
} from '../services/appleIap';
import {
  consumeBillingReturnPending,
  openBillingPortal,
  startProCheckout,
} from '../services/billingService';
import { telemetry } from '../services/telemetry';
import type { PaywallSource } from '../services/telemetryTypes';

function afterBrowserSettled(fn: () => void): void {
  // InAppBrowser / ASWebAuthenticationSession must fully dismiss before any overlay,
  // or touches stay dead on the underlying screen.
  InteractionManager.runAfterInteractions(() => {
    setTimeout(fn, 450);
  });
}

function billingMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * iOS: Apple IAP only (no Stripe Checkout / Portal in the binary).
 * Android and web: Stripe Checkout / Portal. Play Billing is not used.
 */
export function useBillingActions() {
  const { refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const usesAppleIap = Platform.OS === 'ios';

  const afterNativeCheckout = useCallback(async () => {
    if (Platform.OS === 'web') return;
    consumeBillingReturnPending();
    try {
      await refreshUser();
    } catch {
      // best-effort
    }
    afterBrowserSettled(() => openProWelcome());
  }, [refreshUser]);

  const afterNativePortal = useCallback(async () => {
    if (Platform.OS === 'web') return;
    consumeBillingReturnPending();
    try {
      await refreshUser();
    } catch {
      // best-effort
    }
  }, [refreshUser]);

  const afterApplePurchase = useCallback(async () => {
    try {
      await refreshUser();
    } catch {
      // best-effort
    }
    afterBrowserSettled(() => openProWelcome());
  }, [refreshUser]);

  const upgradeToPro = useCallback(async (source: PaywallSource = 'unknown') => {
    setError(null);
    setBusy(true);
    // Native only — InAppBrowser needs Settings gone so it cannot trap touches.
    // Web Checkout is a same-tab redirect; closing Settings first dumps you on Home.
    if (Platform.OS !== 'web') {
      closeSettingsOverlay();
    }
    void telemetry.logBeginCheckout(source);
    try {
      if (usesAppleIap) {
        const result = await purchaseProSubscription();
        if (result === 'success') {
          await afterApplePurchase();
        } else {
          void telemetry.logCheckoutAbandon(source, 'cancel');
        }
        return;
      }

      const result = await startProCheckout();
      if (result === 'success') {
        await afterNativeCheckout();
      } else {
        consumeBillingReturnPending();
        void telemetry.logCheckoutAbandon(source, result);
      }
    } catch (err) {
      if (usesAppleIap && isAppleIapUserCancelled(err)) {
        void telemetry.logCheckoutAbandon(source, 'cancel');
        return;
      }
      void telemetry.logCheckoutAbandon(source, 'error');
      const message = billingMessage(err, 'Could not start checkout');
      setError(message);
      throw err;
    } finally {
      setBusy(false);
    }
  }, [afterApplePurchase, afterNativeCheckout, usesAppleIap]);

  const manageSubscription = useCallback(async () => {
    setError(null);
    setBusy(true);
    if (Platform.OS !== 'web') {
      closeSettingsOverlay();
    }
    try {
      if (usesAppleIap) {
        await openAppleSubscriptions();
        return;
      }

      await openBillingPortal();
      await afterNativePortal();
    } catch (err) {
      const message = billingMessage(err, 'Could not open billing portal');
      setError(message);
      throw err;
    } finally {
      setBusy(false);
    }
  }, [afterNativePortal, usesAppleIap]);

  const restorePurchases = useCallback(async () => {
    if (!usesAppleIap) return;
    setError(null);
    setBusy(true);
    try {
      const restored = await restoreProPurchases();
      await refreshUser();
      if (!restored) {
        setError('No Apple subscription found to restore for this Apple ID.');
      }
    } catch (err) {
      const message = billingMessage(err, 'Could not restore purchases');
      setError(message);
      throw err;
    } finally {
      setBusy(false);
    }
  }, [refreshUser, usesAppleIap]);

  return {
    busy,
    error,
    clearError: () => setError(null),
    usesAppleIap,
    upgradeToPro,
    manageSubscription,
    restorePurchases,
  };
}
